import { useCallback, useMemo, useState } from "react";

const SIZE_OPTIONS = [
  "1 Gal",
  "3 Gal",
  "7 Gal",
  "15 Gal",
  "25 Gal",
  "45 Gal",
  "65 Gal",
  "100 Gal",
  "200 Gal",
] as const;

export type AdditionalItem = {
  plant_name: string;
  size: string;
  quantity: number;
};

export type AdditionalNotePayloadItem = {
  plant_name: string;
  size: string;
  quantity: number;
};

const EMPTY_ROW: AdditionalItem = { plant_name: "", size: "", quantity: 0 };

export function useAdditionalList() {
  const [rows, setRows] = useState<AdditionalItem[]>([EMPTY_ROW]);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, { ...EMPTY_ROW }]);
  }, []);

  const updateRow = useCallback(
    (index: number, field: keyof AdditionalItem, value: string | number) => {
      setRows((prev) =>
        prev.map((row, rowIndex) =>
          rowIndex === index ? { ...row, [field]: value } : row
        )
      );
    },
    []
  );

  const removeRow = useCallback(
    (index: number) => {
      setRows((prev) => {
        if (prev.length <= 1) return prev;
        return prev.filter((_, rowIndex) => rowIndex !== index);
      });
    },
    []
  );

  const reset = useCallback(() => {
    setRows([{ ...EMPTY_ROW }]);
  }, []);

  const notePayload = useMemo<AdditionalNotePayloadItem[]>(
    () =>
      rows
        .filter(
          (item) =>
            item.plant_name.trim() && item.size && Number(item.quantity) > 0
        )
        .map((item) => ({
          plant_name: item.plant_name.trim(),
          size: item.size,
          quantity: Number(item.quantity),
        })),
    [rows]
  );

  return {
    SIZE_OPTIONS,
    rows,
    addRow,
    updateRow,
    removeRow,
    reset,
    notePayload,
    canDelete: rows.length > 1,
  };
}

